import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import AccountPlanPanel from "./AccountPlanPanel";
import { planLanding } from "./landing";
import {
  LastActivity,
  RowActionKind,
  awaitingConfirm,
  lastPersonActivity,
  planRowState,
} from "./planBucket";
import PortfolioQuickLook, {
  currentStep,
  forSponsor,
} from "./PortfolioQuickLook";
import { PERSONA_LABEL } from "./shared";
import SponsorPlansList from "./SponsorPlansList";
import TeamPlansList from "./TeamPlansList";
import VersionUploadDialog from "./VersionUploadDialog";
import {
  useAccountPlanStatuses,
  usePlanActivity,
  usePolling,
} from "../../Hooks/useAccountPlan";
import { uploadPath } from "../../Pages/MyPortfolioAccounts/AccountPlanCell";
import type { OrgAccountRow } from "../../Store/Reducers/AccountPlanSlice";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import type {
  AccountPlanStatus,
  Persona,
  PlanActivityAccount,
  PlanActivityRow,
  PortfolioRoles,
} from "../../Types/AccountPlan";

export { timeAgo } from "./TeamPlansList";

interface Props {
  accounts: PortfolioAccountRow[];
  withOrg: (path: string) => string;
  /** The portfolio itself is still loading: show that, not an empty portfolio. */
  loading?: boolean;
  /**
   * Every account in the organization (Exec Ops → Account Plans, NAV-08), not the
   * user's own portfolio: the empty state says so.
   */
  orgWide?: boolean;
}

const PERSONAS: Persona[] = ["ES", "AE", "CSM"];
/** How often a quiet list looks again for plans someone else started. */
export const IDLE_REFRESH_MS = 60_000;

/** A sponsor's list has no version history to show: no Activity call for them. */
const isSponsorOnly = (me: PortfolioRoles | null) =>
  !!me && !me.admin && me.default === "ES";

/**
 * Date-only strings ("2027-08-01") are calendar dates, not instants: parsing
 * them as UTC midnight shows the previous day/month west of Greenwich.
 */
export const parseCalendarDate = (value: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(value);
};

/**
 * My Accounts → Account Plans (and Exec Ops → Account Plans, org-wide). The sponsor
 * gets the briefing-first list; the account team the status list (TeamPlansList).
 * This component loads the statuses and owns where every click goes.
 */
/** One organization account as a list row (only the fields the list reads). */
const orgRowToPlanRow = (r: OrgAccountRow): PortfolioAccountRow =>
  ({
    customer_id: r.customer_id,
    customer_name: r.customer_name,
    industry: r.industry,
    tier: r.tier,
    revenue: r.revenue,
    renewal_date: r.renewal_date,
  }) as PortfolioAccountRow;

const AccountPlansListTab: React.FC<Props> = ({
  accounts: portfolioAccounts,
  withOrg,
  loading: portfolioLoading = false,
  orgWide = false,
}) => {
  const navigate = useNavigate();
  const [viewAs, setViewAs] = useState<Persona | null>(null);
  const [quickId, setQuickId] = useState<string | null>(null);
  // The account team's row opens the account panel; its upload, the upload dialog.
  const [panelId, setPanelId] = useState<string | null>(null);
  // The failed or rejected version whose upload dialog is open (VAL-28).
  const [fixing, setFixing] = useState<{
    accountId: string;
    fileId: string;
  } | null>(null);
  // My accounts (the portfolio) or All accounts (every account in the organization,
  // Sushant 2026-10-29). Exec Ops is organization-wide already: no switch there.
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const ids = useMemo(
    () => portfolioAccounts.map(a => a.customer_id),
    [portfolioAccounts]
  );
  const mine = useAccountPlanStatuses(ids, scope === "all");
  const all = useAccountPlanStatuses([], scope !== "all", "all");
  const active = scope === "all" ? all : mine;
  const {
    statuses: rowStatuses,
    me: fetchedMe,
    loading,
    error,
    refresh,
  } = active;
  const accounts = useMemo(
    () =>
      scope === "all"
        ? (all.rows || []).map(orgRowToPlanRow)
        : portfolioAccounts,
    [scope, all.rows, portfolioAccounts]
  );
  // Who the viewer is outlives a change of account list (a new statuses key starts
  // empty): the layout must not blink out, losing open menus, while it reloads.
  const knownMe = useRef(fetchedMe);
  if (fetchedMe) knownMe.current = fetchedMe;
  const me = fetchedMe || knownMe.current;

  // Keep the list moving while a plan is read or a briefing written; the
  // statuses endpoint isn't polled anywhere else.
  const working = Object.keys(rowStatuses).some(
    id =>
      rowStatuses[id]?.latest_file?.status === "processing" ||
      !!rowStatuses[id]?.briefing_queue
  );
  usePolling(working, refresh);
  // Nothing moving on this list: still look again now and then, and when the tab
  // comes back, so a plan someone else starts shows up without a reload (a list
  // opened before the upload used to stay on "No plan" for good).
  useEffect(() => {
    if (working) return undefined;
    const tick = () => {
      if (typeof document === "undefined" || !document.hidden) refresh();
    };
    const timer = window.setInterval(tick, IDLE_REFRESH_MS);
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [working, refresh]);

  // What happened to each account's plans (Activity, folded into this tab on
  // 2026-10-29): who moved it last, and each version's steps for the panel.
  const { data: activity } = usePlanActivity("all", isSponsorOnly(me));
  const activityBy = useMemo(() => {
    const out: Record<string, PlanActivityAccount> = {};
    (activity?.accounts || []).forEach(a => {
      out[a.account_id] = a;
    });
    return out;
  }, [activity]);
  const journeysFor = (id: string): Record<string, PlanActivityRow> => {
    const a = activityBy[id];
    const out: Record<string, PlanActivityRow> = {};
    [a?.latest, ...(a?.earlier || [])].forEach(r => {
      if (r?.file_id) out[r.file_id] = r;
    });
    return out;
  };
  // Last Activity: the newest thing a PERSON did (system steps don't count).
  const lastActivity = useMemo(() => {
    const out: Record<string, LastActivity> = {};
    Object.keys(activityBy).forEach(id => {
      const last = lastPersonActivity(activityBy[id]);
      if (last) out[id] = last;
    });
    return out;
  }, [activityBy]);

  const admin = !!me?.admin;
  const defaultPersona: Persona = me?.default || "AE";
  // Only admins may look at another persona's layout.
  const persona: Persona = (admin && viewAs) || defaultPersona;
  const isES = persona === "ES";
  // The sponsor's layout describes the version each sponsor reads: the newest
  // one with a briefing (F-44 = B, 2026-10-19).
  const statuses = useMemo(() => {
    if (!isES) return rowStatuses;
    const out: Record<string, AccountPlanStatus> = {};
    Object.keys(rowStatuses).forEach(id => {
      out[id] = forSponsor(rowStatuses[id]) as AccountPlanStatus;
    });
    return out;
  }, [isES, rowStatuses]);
  const holds = useCallback(
    (id: string, role: Persona) =>
      admin || (me?.by_account[id] || []).indexOf(role) >= 0,
    [admin, me]
  );
  // An admin holds every role: by default they act as both, and "Viewing as"
  // narrows them to one. Everyone else acts in the roles they hold on the account.
  const rolesOn = useCallback(
    (id: string): Persona[] => {
      // View-only roles act in none (2026-11-03): their rows offer View plan, never an upload.
      if (me?.read_only) return [];
      if (admin) return viewAs && viewAs !== "ES" ? [viewAs] : ["AE", "CSM"];
      return (["AE", "CSM"] as Persona[]).filter(r => holds(id, r));
    },
    [admin, viewAs, holds, me?.read_only]
  );

  // Whether the plan page lets this viewer in: someone on the account (or an admin).
  // Under All accounts the rest see status and history, not the plan.
  const canOpenPlan = (id: string) =>
    admin || (me?.by_account[id] || []).length > 0;

  // --- navigation ---
  // `as` is only needed when an admin is looking at a non-default persona.
  const asParam = admin && persona !== defaultPersona ? persona : null;
  const go = (id: string, q: Record<string, string | null> = {}) => {
    const qs = Object.keys(q)
      .filter(k => q[k])
      .map(k => `${k}=${encodeURIComponent(q[k] as string)}`)
      .join("&");
    navigate(withOrg(`/account-plan/${id}${qs ? `?${qs}` : ""}`));
  };
  const openBriefing = (id: string) => go(id, { tab: "sponsor", as: "ES" });
  const openCurrent = (id: string) => go(id, { as: asParam });
  // AE/CSM reading a published briefing as their own page (view=briefing — no
  // "previewing" banner), with a button to their review workspace.
  const openSponsorView = (id: string) =>
    go(id, { tab: "sponsor", view: "briefing", as: asParam });
  // A row opens where the plan page itself would land (spec NAV-01, the shared
  // `planLanding` rule): Review draft until the plan is published, then the
  // read-only briefing; Plan when there is no plan yet.
  const openRow = (id: string) => {
    if (isES) return openBriefing(id);
    const s = statuses[id];
    const landing = planLanding(persona, {
      hasPlan: !!s?.latest_file,
      published: s?.review_status === "published",
      toConfirm: awaitingConfirm(s),
      // The same rule as the page and Quick look: nothing usable yet → Plan.
      reading: s?.latest_file?.status === "processing" && !s.viewer_ready,
    });
    if (landing.view) return openSponsorView(id);
    // No plan: the page's own default (Plan) — no tab in the URL.
    if (landing.tab === "plan") return openCurrent(id);
    return go(id, { tab: landing.tab, as: asParam });
  };
  const openTab = (
    id: string,
    tab: "plan" | "viewer" | "sponsor" | "publish"
  ) => go(id, { tab, as: asParam });
  // Quick look: its "Go to <step>" button opens exactly that step. On this list it
  // opens only for the sponsor and for accounts the viewer isn't on, so it offers no
  // Preview as sponsor (F-UF-F24): the plan page's Review briefing and Publish steps
  // have it, and they show exactly what the sponsor sees.
  const openStep = (id: string) => openTab(id, currentStep(statuses[id]));

  /** The row's one button. */
  const onAction = (id: string, kind: RowActionKind) => {
    const s = statuses[id];
    switch (kind) {
      // As Account Priorities' Add / Update do: the plan page's upload step.
      case "upload":
        return navigate(withOrg(uploadPath(id)));
      // "Take action" / "Go to the problem": the AE lands on the upload step with
      // the problem highlighted (`focus=problem` is the plan page's contract).
      // Anyone else reads why in the read-only upload dialog.
      case "fix":
        if (rolesOn(id).indexOf("AE") >= 0)
          return navigate(withOrg(`${uploadPath(id)}&focus=problem`));
        if (s?.latest_file)
          setFixing({ accountId: id, fileId: s.latest_file.id });
        return undefined;
      // Confirm plan: the AE's step 2 (the plan page shows the confirm panel there).
      case "confirm":
        return go(id, { tab: "viewer", as: "AE" });
      case "draft":
      case "verify":
        return openTab(id, "sponsor");
      case "publish":
        return openTab(id, "publish");
      case "briefing":
        return openSponsorView(id);
      default:
        return openRow(id);
    }
  };

  /** A panel button: the panel closes first (it leaves the page, or opens a dialog). */
  const fromPanel = (fn: (id: string) => void) => {
    if (!panelId) return;
    setPanelId(null);
    fn(panelId);
  };

  const personaSwitch = admin ? (
    <div
      role="group"
      aria-label="Viewing as"
      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-0.5"
    >
      <span className="px-1.5 text-xs font-medium text-gray-500">
        Viewing as
      </span>
      {PERSONAS.map(p => (
        <button
          key={p}
          type="button"
          aria-pressed={persona === p}
          title={PERSONA_LABEL[p]}
          onClick={() => setViewAs(p)}
          className={`rounded-md px-2.5 py-1 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${persona === p ? "bg-[#974ae4] text-white" : "text-gray-600 hover:bg-gray-100"}`}
        >
          {p}
        </button>
      ))}
    </div>
  ) : null;

  const panelAccount = panelId
    ? accounts.find(a => a.customer_id === panelId)
    : undefined;
  const quickAccount = quickId
    ? accounts.find(a => a.customer_id === quickId)
    : undefined;

  // Until the server says who the viewer is, no layout: a sponsor would otherwise
  // see the account team's status list flash up first.
  if (!me && loading)
    return (
      <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-[#974ae4]" aria-hidden />
        Loading account plans…
      </p>
    );

  return (
    <>
      {isES ? (
        <SponsorPlansList
          accounts={accounts}
          statuses={statuses}
          portfolioLoading={portfolioLoading}
          loading={loading}
          error={error}
          refresh={refresh}
          personaSwitch={personaSwitch}
          openBriefing={openBriefing}
          openQuickLook={setQuickId}
          parseCalendarDate={parseCalendarDate}
          // Published-only roles (SE, TAM…) read briefings like a sponsor, but aren't one.
          publishedOnly={me?.access === "published"}
        />
      ) : (
        <TeamPlansList
          accounts={accounts}
          statuses={statuses}
          rolesOn={rolesOn}
          portfolioLoading={
            scope === "all" ? all.loading && !all.rows : portfolioLoading
          }
          loading={loading}
          error={error}
          refresh={refresh}
          orgWide={orgWide || scope === "all"}
          personaSwitch={personaSwitch}
          openRow={setPanelId}
          onAction={onAction}
          openQuickLook={setQuickId}
          canUpload={id => rolesOn(id).indexOf("AE") >= 0}
          canOpenPlan={canOpenPlan}
          lastActivity={lastActivity}
          scope={orgWide ? null : scope}
          onScope={v => {
            setPanelId(null);
            setScope(v);
          }}
          parseCalendarDate={parseCalendarDate}
        />
      )}

      <AccountPlanPanel
        account={panelAccount || null}
        status={panelId ? statuses[panelId] : undefined}
        row={
          panelId && statuses[panelId]
            ? planRowState(statuses[panelId], rolesOn(panelId))
            : undefined
        }
        journeys={panelId ? journeysFor(panelId) : {}}
        canUpload={!!panelId && rolesOn(panelId).indexOf("AE") >= 0}
        canOpen={!!panelId && canOpenPlan(panelId)}
        onClose={() => setPanelId(null)}
        onAction={kind => fromPanel(id => onAction(id, kind))}
        onUpload={() => fromPanel(id => onAction(id, "upload"))}
        onReplaceDraft={() =>
          fromPanel(id => navigate(withOrg(`${uploadPath(id)}&replace=1`)))
        }
        openProblem={() => fromPanel(id => onAction(id, "fix"))}
        openPlan={() => fromPanel(openRow)}
        openViewer={fileId =>
          fromPanel(id => go(id, { tab: "viewer", file: fileId, as: asParam }))
        }
        parseCalendarDate={parseCalendarDate}
      />

      <PortfolioQuickLook
        account={
          quickAccount
            ? { id: quickAccount.customer_id, name: quickAccount.customer_name }
            : null
        }
        status={quickId ? statuses[quickId] : undefined}
        persona={persona}
        admin={admin}
        onClose={() => setQuickId(null)}
        openBriefing={openBriefing}
        openCurrent={openStep}
        openReading={openSponsorView}
        canOpen={!quickId || canOpenPlan(quickId)}
      />

      {fixing && (
        <VersionUploadDialog
          accountId={fixing.accountId}
          fileId={fixing.fileId}
          filename=""
          canEdit={holds(fixing.accountId, "AE")}
          onClose={() => setFixing(null)}
          onChanged={refresh}
          onOpenPlan={() => {
            setFixing(null);
            // "Open plan" / "Go to Confirm plan": the read upload waits there (Phase 2).
            openTab(fixing.accountId, "viewer");
          }}
        />
      )}
    </>
  );
};

export default AccountPlansListTab;
